import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { CMSSection } from '@/cms/CMSSection';
import { Container } from '@/components/Container';
import type { SectionHeaderProps } from '@/components/SectionHeader';
import { SectionHeader } from '@/components/SectionHeader';
import type {
  ContentfulBannerFragment,
  ContentfulImageWithFocalPoint,
} from '@/graphql/contentful/api';
import { generateDataTestId } from '@/lib/tests';

import * as S from './Banner.styles';

export interface BannerProps extends ContentfulBannerFragment {
  preload?: boolean;
}

export const Banner = ({
  backgroundColor,
  bannerText: text,
  heading,
  image,
  imagePosition = 'left',
  textAlignment,
  url,
  urlText,
  urlType,
}: BannerProps) => {
  const textAlign = textAlignment || 'center';
  image = image as ContentfulImageWithFocalPoint;

  return (
    <CMSSection
      data-test-id='BannerSection'
      {...generateDataTestId('banner', 'section')}
    >
      <Container disableGuttersDesktop>
        <S.Wrapper
          backgroundColor={backgroundColor?.value}
          data-test-id='BannerWrapper'
          imagePosition={imagePosition}
          {...generateDataTestId('banner', 'wrapper')}
        >
          {image && (
            <S.Image
              alt={image.alt ?? 'Banner'}
              data-test-id='BannerImage'
              fill
              sizes='50vw'
              src={image.image?.url ?? ''}
            />
          )}
          <S.Content
            {...generateDataTestId('banner', 'content')}
            data-test-id='BannerContent'
            textAlignment={textAlign}
          >
            {heading && (
              <SectionHeader
                colorized={false}
                disableGutters
                sizeDesktop={50}
                sizeMobile={40}
                text={heading}
                textAlign={textAlignment as SectionHeaderProps['textAlign']}
              />
            )}
            {text && (
              <S.Text
                dangerouslySetInnerHTML={{
                  __html: documentToHtmlString(text.json),
                }}
                data-test-id='BannerText'
              />
            )}

            {urlText &&
              (urlType === 'button' ? (
                <S.CTA
                  data-test-id='BannerCTAButton'
                  data-testid='BannerCTAButton'
                  label={urlText}
                  linkProps={{ href: url || '#' }}
                  variant='filled'
                />
              ) : (
                <S.CTA
                  ariaLabel={getLinkAriaLabel(url || '')}
                  data-test-id='BannerCTALink'
                  data-testid='BannerCTALink'
                  label={urlText}
                  linkProps={{ href: url || '#' }}
                  variant='link'
                >
                  <S.Chevron />
                </S.CTA>
              ))}
          </S.Content>
        </S.Wrapper>
      </Container>
    </CMSSection>
  );
};

Banner.displayName = 'Banner';
